import React from 'react'
import { match } from 'ts-pattern'

import { SurveyGroupAnswerOptionMultiSelector } from '~/components/features/SurveyGroupAnswerOptionMultiSelector'
import {
  SurveyGroupAnswerOptionSelector,
  type SurveyGroupAnswerOptionSelectorProps,
} from '~/components/features/SurveyGroupAnswerOptionSelector'
import {
  type ConditionOperation,
  type SurveyGroupAnswerOption as SurveyGroupAnswerOptionBase,
} from '~/gql/graphql'
import { unassignedSymbol } from '~/utils'

type SurveyGroupAnswerOption = App.PickGraphQL<
  SurveyGroupAnswerOptionBase,
  'option' | 'text'
>

export type SurveyGroupAnswerOptionConditionalSelectorProps<
  TSurveyGroupAnswerOption extends SurveyGroupAnswerOption,
> = {
  answerOptions: TSurveyGroupAnswerOption[]
  className?: string
  conditionOperation: ConditionOperation
  disabled?: boolean
  onBlur?: () => void
  onChange?: (options: TSurveyGroupAnswerOption['option'][]) => void
  value: TSurveyGroupAnswerOption['option'][]
}

export const SurveyGroupAnswerOptionConditionalSelector = React.forwardRef(
  <TSurveyGroupAnswerOption extends SurveyGroupAnswerOption>(
    {
      answerOptions,
      className,
      conditionOperation,
      disabled,
      onBlur,
      onChange,
      value,
    }: SurveyGroupAnswerOptionConditionalSelectorProps<TSurveyGroupAnswerOption>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const handleAnswerOptionChange = React.useCallback<
      EH<
        SurveyGroupAnswerOptionSelectorProps<TSurveyGroupAnswerOption>,
        'onChange'
      >
    >(
      (option) => {
        if (option !== unassignedSymbol) {
          onChange?.([option])
        }
      },
      [onChange],
    )

    return (
      <>
        {match(conditionOperation)
          .with('equal', 'not_equal', () => (
            <SurveyGroupAnswerOptionSelector
              ref={ref}
              answerOptions={answerOptions}
              className={className}
              disabled={disabled}
              value={value[0]}
              onBlur={onBlur}
              onChange={handleAnswerOptionChange}
            />
          ))
          .with('in', 'not_in', () => (
            <SurveyGroupAnswerOptionMultiSelector
              ref={ref}
              answerOptions={answerOptions}
              className={className}
              disabled={disabled}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          ))
          .exhaustive()}
      </>
    )
  },
)
